import { supabase } from '../../../integrations/supabase/client';

export interface Lead {
  id: string;
  user_id: string;
  numero_telefono: string;
  telefono_lead?: string | null;
  name?: string | null;
  email?: string | null;
  company?: string | null;
  tags?: string | null;
  stage_id?: string | null;
  assigned_agent_id?: string | null;
  assigned_user_id?: string | null;
  flow_id?: string | null;
  last_message?: string | null;
  last_interaction_at?: string | null;
  notes?: string | null;
  value?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  ia_activa?: boolean; // Controla si la IA responde para este lead
}

export interface LeadWithAgent extends Lead {
  agent_name?: string;
}

export const leadsApi = {
  // Obtener leads con filtros opcionales
  async getLeads(
    flowId?: string,
    agentId?: string
  ): Promise<LeadWithAgent[]> {
    try {
      let query = supabase
        .from('leads')
        .select(`
          *,
          agents!leads_assigned_agent_id_fkey(nombre)
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user!.id)
        .order('updated_at', { ascending: false });

      if (flowId) {
        query = query.eq('flow_id', flowId);
      }

      if (agentId) {
        query = query.eq('assigned_agent_id', agentId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map((lead: any) => ({
        ...lead,
        agent_name: lead.agents?.nombre || 'Sin asignar',
      }));
    } catch (error) {
      console.error('[leadsApi] Error fetching leads:', error);
      throw error;
    }
  },

  // Obtener leads agrupados por etapa (usando tags)
  async getLeadsByStage(
    stageName: string,
    flowId?: string
  ): Promise<LeadWithAgent[]> {
    try {
      let query = supabase
        .from('leads')
        .select(`
          *,
          agents!leads_assigned_agent_id_fkey(nombre)
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user!.id)
        .eq('tags', stageName)
        .order('updated_at', { ascending: false });

      if (flowId) {
        query = query.eq('flow_id', flowId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map((lead: any) => ({
        ...lead,
        agent_name: lead.agents?.nombre || 'Sin asignar',
      }));
    } catch (error) {
      console.error('[leadsApi] Error fetching leads by stage:', error);
      throw error;
    }
  },

  // Actualizar la etapa de un lead (actualiza el campo tags)
  async updateLeadStage(leadId: string, stageName: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ tags: stageName })
        .eq('id', leadId);

      if (error) throw error;
    } catch (error) {
      console.error('[leadsApi] Error updating lead stage:', error);
      throw error;
    }
  },

  // Actualizar información del lead
  async updateLead(leadId: string, updates: Partial<Lead>): Promise<Lead> {
    try {
      const { data, error } = await supabase
        .from('leads')
        .update(updates)
        .eq('id', leadId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[leadsApi] Error updating lead:', error);
      throw error;
    }
  },

  // Crear un lead
  async createLead(lead: Partial<Lead>): Promise<Lead> {
    try {
      const userId = (await supabase.auth.getUser()).data.user!.id;

      // Asegurar que numero_telefono esté presente
      if (!lead.numero_telefono) {
        throw new Error('numero_telefono es requerido');
      }

      // Check if lead already exists with same unique constraint
      const { data: existing } = await supabase
        .from('leads')
        .select('id')
        .eq('user_id', userId)
        .eq('telefono_lead', lead.telefono_lead || '')
        .eq('assigned_agent_id', lead.assigned_agent_id || '')
        .maybeSingle();

      if (existing) {
        // Update existing lead instead of inserting
        const { data, error } = await supabase
          .from('leads')
          .update({
            numero_telefono: lead.numero_telefono,
            name: lead.name,
            email: lead.email,
            company: lead.company,
            tags: lead.tags || 'Nuevo',
            stage_id: lead.stage_id,
            flow_id: lead.flow_id,
            last_message: lead.last_message,
            last_interaction_at: lead.last_interaction_at,
            notes: lead.notes,
            value: lead.value,
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      }

      const { data, error } = await supabase
        .from('leads')
        .insert([{
          user_id: userId,
          numero_telefono: lead.numero_telefono,
          telefono_lead: lead.telefono_lead,
          name: lead.name,
          email: lead.email,
          company: lead.company,
          tags: lead.tags || 'Nuevo',
          stage_id: lead.stage_id,
          assigned_agent_id: lead.assigned_agent_id,
          assigned_user_id: lead.assigned_user_id,
          flow_id: lead.flow_id,
          last_message: lead.last_message,
          last_interaction_at: lead.last_interaction_at,
          notes: lead.notes,
          value: lead.value,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[leadsApi] Error creating lead:', error);
      throw error;
    }
  },

  // Eliminar un lead
  async deleteLead(leadId: string): Promise<void> {
    try {
      const { error } = await supabase.from('leads').delete().eq('id', leadId);

      if (error) throw error;
    } catch (error) {
      console.error('[leadsApi] Error deleting lead:', error);
      throw error;
    }
  },
};
